'use client';

import React from 'react';

import Image from 'next/image';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import aa from 'search-insights';

import backToOfficeArrow from '@/assets/icons/arrow-narrow-left.svg';
import { useShoppingCartContext } from '@/components/Providers/ShoppingCartProvider';
import Container from '@/components/V2/Container';
import { useProductContext } from '@/components/V2/Providers/ProductProvider';
import { ALGOLIA_INDEX_NAME } from '@/config/env';
import { ShoppingFlowType } from '@/helpers/constants/shopping';
import cn from '@/helpers/utils/classNames';
import { roundToTwoDecimalPlaces } from '@/helpers/utils/numberHelpers';
import { useAppSelector } from '@/store';
import { ProductStrapi } from '@/types/Product';

import NewProductPreview, { ActionButtons, ImageGallery, ProductInfo } from '../NewProductPreview';

interface NewProductDetailProps {
  product: ProductStrapi;
  productSlug?: string;
  isShoppablePage?: boolean;
}
const NewProductDetail = ({ product, isShoppablePage }: NewProductDetailProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { onAddToCart, onAddToCartUsingPawPoints, onAddToCartUsingSampleCredits, isLoading, updateShoppingFlowType } =
    useShoppingCartContext();
  const {
    product: strapiProduct,
    selectedSize,
    sizes,
    onSizeChange,
    selectedNestedSize,
    nestedSizes,
  } = useProductContext();
  const cookieConsent = useAppSelector(state => state.slices.appConfig.isCookieConsentRejected);

  const titleParam = searchParams.get('backToTitle');
  const returnUrlParam = searchParams.get('ReturnUrl');
  const algoliaQueryID = searchParams.get('queryID') || '';

  const returnUrl = returnUrlParam ? decodeURIComponent(returnUrlParam).replaceAll('%26', '&') : null;
  const title = titleParam ? decodeURI(titleParam) : null;

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const sendAlgoliaEvents = (product: ProductStrapi, quantity: number) => {
    if (cookieConsent !== true) return;
    const event = !!algoliaQueryID ? 'addedToCartObjectIDsAfterSearch' : 'addedToCartObjectIDs';
    aa('getUserToken', {}, (err, userToken) => {
      if (err) {
        console.error(err);
        return;
      }

      aa('getAuthenticatedUserToken', {}, (err, authenticatedUserToken) => {
        if (err) {
          console.error(err);
          return;
        }
        aa(event, {
          userToken: `${userToken || ''}`,
          authenticatedUserToken: `${authenticatedUserToken || ''}`,
          index: ALGOLIA_INDEX_NAME,
          eventName: 'Product Added To Cart',
          objectIDs: [product?.attributes?.ItemCode],
          objectData: [
            {
              price: product?.attributes?.Prices?.RetailPrice,
              quantity: quantity,
              ...(!!algoliaQueryID ? { queryID: algoliaQueryID } : {}),
            },
          ],
          value: +roundToTwoDecimalPlaces(quantity * +(product?.attributes?.Prices?.RetailPrice || '0')),
          currency: 'USD',
          ...(!!algoliaQueryID ? { queryID: algoliaQueryID } : {}),
        });
        console.log('sent event to algolia', { event });
      });
    });
  };

  return (
    <>
      <Container
        className={cn(
          'mb-[150px] sm:mb-[120px] md:mb-[150px]',
          isShoppablePage &&
            'v2-font-sweet-sans-pro sm:!v2-px-0 xs:!v2-px-0 mb-[120px] sm:mb-[80px] md:mb-[120px] xs:mb-[80px]'
        )}
      >
        <div className={cn(!isShoppablePage && 'v2-pb-[16px] v2-pt-6', isShoppablePage && 'v2-pb-[0px] v2-pt-0')}>
          {!isShoppablePage && (
            <>
              {returnUrl ? (
                <Link
                  href={returnUrl}
                  className="v2-bg-transparent v2-flex v2-gap-[6px] v2-font-sweet-sans-pro v2-font-normal v2-text-primary v2-text-sm"
                >
                  {/*eslint-disable-next-line sonarjs/no-duplicate-string */}
                  <Image src={backToOfficeArrow} alt={title || 'Back to Shopping'} />
                  {title || 'Back to Shopping'}
                </Link>
              ) : (
                <button
                  onClick={() => (returnUrl ? router.push(returnUrl) : router.back())}
                  className="v2-bg-transparent v2-flex v2-gap-[6px] v2-font-sweet-sans-pro v2-font-normal v2-text-primary v2-text-sm"
                >
                  <Image src={backToOfficeArrow} alt={title || 'Back to Shopping'} />
                  {title || 'Back to Shopping'}
                </button>
              )}
            </>
          )}
        </div>
        <NewProductPreview
          sizes={sizes}
          selectedSize={selectedSize}
          onSizeChange={onSizeChange}
          selectedNestedSize={selectedNestedSize}
          nestedSizes={nestedSizes}
          product={strapiProduct || ({} as ProductStrapi)}
          productDetailPageLink=""
          isProductDetailPage
          isShoppablePage={isShoppablePage}
        >
          <div className="v2-mx-auto v2-grid v2-grid-cols-1 v2-gap-[30px] md:v2-grid-cols-2 lg:v2-grid-cols-2 xl:v2-grid-cols-2">
            <div className="">
              <ImageGallery />
            </div>
            <div className="v2-flex v2-h-full v2-flex-col">
              <ProductInfo isShoppablePage={isShoppablePage} />

              <ActionButtons
                key={product?.attributes?.ItemCode}
                onAddToCart={(product, quantity, isEZShip) => {
                  updateShoppingFlowType(
                    isShoppablePage ? ShoppingFlowType.ShoppableLandingPages : ShoppingFlowType.RegularShopping
                  );
                  onAddToCart(product, quantity, isEZShip, algoliaQueryID);
                  sendAlgoliaEvents(product, quantity);
                }}
                onAddToCartUsingPawPoints={(product, quantity, isEZShip) => {
                  updateShoppingFlowType(
                    isShoppablePage ? ShoppingFlowType.ShoppableLandingPages : ShoppingFlowType.RegularShopping
                  );
                  onAddToCartUsingPawPoints(product, quantity, isEZShip, algoliaQueryID);
                  sendAlgoliaEvents(product, quantity);
                }}
                onAddToCartUsingSampleCredits={(product, quantity, isEZShip) => {
                  updateShoppingFlowType(
                    isShoppablePage ? ShoppingFlowType.ShoppableLandingPages : ShoppingFlowType.RegularShopping
                  );
                  onAddToCartUsingSampleCredits(product, quantity, isEZShip, algoliaQueryID);
                  sendAlgoliaEvents(product, quantity);
                }}
                isAddingToCart={isLoading}
                isShoppablePage={isShoppablePage}
              />
            </div>
          </div>
        </NewProductPreview>
      </Container>
    </>
  );
};

export default NewProductDetail;
